import AdminPageHero from "../components/admin/AdminPageHero";
import useAdminOperation from "../hooks/useAdminOperation";
import { verifyProductSave, variantPayloadFromFresh, verifyVariantSave, UNVERIFIED_SAVE } from "../lib/inventorySaveVerification";
import { useSearchParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Barcode,
  ClipboardList,
  PackagePlus,
  Pencil,
  Printer,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import BarcodeScanner from "../components/BarcodeScanner";
import { api, formatApiError } from "../lib/api";
import {
  dollarsToMinor,
  fetchAdminFile,
  inventoryApi,
  minorToDollars,
} from "../lib/inventoryApi";
import { variantAvailability } from "../lib/variantAvailability";
import { GALLERY_CATEGORIES } from "../data/siteContent";
import "./AdminInventory.css";

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "General",
  sku: "",
  barcode_value: "",
  price: "",
  sale_price: "",
  cost: "",
  currency: "USD",
  reorder_threshold: "0",
  active: true,
  track_inventory: true,
  fulfillment_mode: "hybrid",
  product_type: "round_sign",
};

const DEFAULT_PRICES = {
  round_sign: 2500,
  shadow_box: 1500,
  swing: 2500,
  mason_jar: null,
  other: null,
};

function inventoryPreviewRows(preview) {
  if (!preview) return [];
  if (Array.isArray(preview.variations)) {
    return preview.variations.map((variation) => ({
      ...variation,
      product: variation.product || variation.product_name || "Product",
      website_quantity:
        variation.website_quantity ?? variation.website_on_hand ?? null,
    }));
  }
  return (preview.products || []).flatMap((product) =>
    (product.variations || []).map((variation) => ({
      ...variation,
      product: product.name || product.sku || "Product",
      website_quantity:
        variation.website_quantity ?? variation.website_on_hand ?? null,
    })),
  );
}

function inventoryPreviewStatus(row) {
  const conflicts = Array.isArray(row.conflicts)
    ? row.conflicts.filter(Boolean)
    : [];
  if (row.conflict) return row.conflict;
  if (conflicts.length) return conflicts.join(", ");
  if (!row.track_inventory) return "Skipped";
  if (row.mapping_status === "ambiguous") return "Ambiguous";
  if (!row.square_variation_id && row.mapping_status !== "untracked") {
    return `Mapping ${row.mapping_status || "missing"}`;
  }
  return row.needs_sync ? "Will update" : "In sync";
}

function inventoryPreviewStatusClass(row) {
  const status = inventoryPreviewStatus(row).toLowerCase();
  return status.includes("ambiguous") ||
    status.includes("conflict") ||
    status.includes("mapping") ||
    status.includes("unknown")
    ? "text-rose-300"
    : "text-emerald-300";
}

function errorText(error) {
  return formatApiError(error) || "The request could not be completed.";
}

function productInventoryQuantity(product) {
  if (product.product_type === "mason_jar") {
    return (product.variants || [])
      .filter((variant) => variant.active !== false && variant.track_inventory)
      .reduce((total, variant) => total + Math.max(0, Number(variant.on_hand || 0) - Number(variant.reserved || 0)), 0);
  }
  return Number(product.available ?? (Number(product.on_hand || 0) - Number(product.reserved || 0)));
}

function isPriced(product) {
  return product.product_type === "mason_jar"
    ? !product.needs_pricing && Number.isFinite(product.from_price_minor) && product.from_price_minor > 0
    : !product.needs_pricing && Number.isFinite(product.price_minor) && product.price_minor > 0;
}

export function productToForm(product) {
  return {
    name: product.name || "",
    description: product.description || "",
    category: product.category || "General",
    sku: product.sku || "",
    barcode_value: product.barcode_value || "",
    price:
      product.price_minor == null ? "" : minorToDollars(product.price_minor),
    sale_price:
      product.sale_price_minor == null
        ? ""
        : minorToDollars(product.sale_price_minor),
    cost: product.cost_minor == null ? "" : minorToDollars(product.cost_minor),
    currency: product.currency || "USD",
    reorder_threshold: String(product.reorder_threshold ?? 0),
    active: product.active !== false,
    track_inventory: product.track_inventory !== false,
    fulfillment_mode: product.fulfillment_mode || "hybrid",
    product_type: product.product_type || "other",
    customization_json: product.customization
      ? JSON.stringify(product.customization, null, 2)
      : "",
  };
}

export function buildProductPayload(form, editing) {
  const typedPrice = dollarsToMinor(form.price);
  const price =
    form.price === ""
      ? (DEFAULT_PRICES[form.product_type] ?? null)
      : typedPrice;
  const sale = form.sale_price === "" ? null : dollarsToMinor(form.sale_price);
  const cost = form.cost === "" ? null : dollarsToMinor(form.cost);
  const threshold = Number(form.reorder_threshold);
  if (
    (form.price !== "" && typedPrice === null) ||
    (form.sale_price !== "" && sale === null) ||
    (form.cost !== "" && cost === null) ||
    !Number.isSafeInteger(threshold) ||
    threshold < 0
  ) {
    throw new Error(
      "Enter valid dollar amounts and a whole-number reorder threshold.",
    );
  }
  let customization = undefined;
  if (editing && form.customization_json?.trim()) {
    try {
      customization = JSON.parse(form.customization_json);
    } catch {
      throw new Error("Customization must be valid JSON.");
    }
  }
  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    category: form.category.trim(),
    active: form.active,
    track_inventory: form.track_inventory,
    fulfillment_mode: form.fulfillment_mode,
    product_type: form.product_type,
    ...(editing && customization ? { customization } : {}),
    price_minor: price,
    sale_price_minor: sale,
    cost_minor: cost,
    currency: form.currency.trim().toUpperCase(),
    reorder_threshold: threshold,
  };
  if (!editing) {
    payload.sku = form.sku.trim();
    if (form.barcode_value.trim())
      payload.barcode_value = form.barcode_value.trim();
  }
  return payload;
}

export default function AdminInventory() {
  const beginOperation = useAdminOperation();
  const [searchParams] = useSearchParams();
  const targetProductId = searchParams.get("product") || "";
  const targetGalleryId = searchParams.get("gallery") || "";
  const targetVariantId = searchParams.get("variant") || "";
  const [targetError, setTargetError] = useState("");
  const [targetLoading, setTargetLoading] = useState(false);
  const stockFilter = ["in", "low", "out", "attention"].includes(searchParams.get("stock")) ? searchParams.get("stock") : "";
  const pricingFilter = ["needs", "priced"].includes(searchParams.get("pricing")) ? searchParams.get("pricing") : "";
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    product_type: "",
    active: "",
    stock: stockFilter,
    pricing: pricingFilter,
  });
  useEffect(() => {
    setFilters({ search: "", category: "", product_type: "", active: "", stock: stockFilter, pricing: pricingFilter });
  }, [stockFilter, pricingFilter]);
  const [sort, setSort] = useState("name_asc");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [lookup, setLookup] = useState("");
  const [operation, setOperation] = useState({
    operation: "receive",
    quantity: "",
    correction_delta: "",
    note: "",
    idempotency_key: "",
  });
  const [history, setHistory] = useState(null);
  const [barcodeUrl, setBarcodeUrl] = useState("");
  const [variantBarcodeUrls, setVariantBarcodeUrls] = useState({});
  const [presetPreview, setPresetPreview] = useState(null);
  const [presetBusy, setPresetBusy] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [presetConfirmed, setPresetConfirmed] = useState(false);
  const [masonConfirmed, setMasonConfirmed] = useState(false);
  const [masonOverwrite, setMasonOverwrite] = useState(false);
  const [squarePreview, setSquarePreview] = useState(null);
  const [squareStatus, setSquareStatus] = useState(null);
  const [barcodeGenerationPreview, setBarcodeGenerationPreview] =
    useState(null);
  const [squareBusy, setSquareBusy] = useState(false);
  const [squareSyncConfirmed, setSquareSyncConfirmed] = useState(false);
  const [inventoryPreview, setInventoryPreview] = useState(null);
  const [inventorySyncConfirmed, setInventorySyncConfirmed] = useState(false);
  const [barcodeGenerationConfirmed, setBarcodeGenerationConfirmed] =
    useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const lookupRef = useRef(null);
  const catalogReturn = useRef(null);
  const returnToCatalog = useRef(false);
  useEffect(() => {
    if (editing || showCreateForm || !returnToCatalog.current) return;
    returnToCatalog.current = false;
    const context = catalogReturn.current;
    const row = context?.productId
      ? Array.from(document.querySelectorAll("[data-inventory-product]")).find(element => element.getAttribute("data-inventory-product") === String(context.productId))
      : null;
    if (row) {
      row.querySelector("button")?.focus?.({ preventScroll: true });
      window.scrollTo?.({ top: context.scrollY, left: 0, behavior: "instant" });
    } else {
      const catalog = document.getElementById("inventory-catalog");
      catalog?.scrollIntoView?.({ block: "start", behavior: "instant" });
      catalog?.focus?.({ preventScroll: true });
    }
    catalogReturn.current = null;
  }, [editing, showCreateForm]);
  const closeEditor = () => {
    returnToCatalog.current = true;
    setShowCreateForm(false);
    setSelected(null);
    setEditing(false);
  };
  useEffect(() => {
    if ((editing || showCreateForm) && window.matchMedia?.("(max-width: 767px)").matches) {
      document.getElementById("inventory-editor")?.scrollIntoView?.({ block: "start", behavior: "auto" });
    }
  }, [editing, showCreateForm, selected?.id]);

  const loadSequence = useRef(0);
  const loadProducts = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const sessionCurrent = beginOperation();
    const operationCurrent = () => sessionCurrent() && sequence === loadSequence.current;
    if (!operationCurrent()) return;
    setLoading(true);
    setLoadError("");
    try {
      // Fetch the full inventory (no server filters); filtering/sorting happen
      // client-side so filters combine predictably and category options always
      // reflect the complete catalogue.
      const allProducts = [];
      let page;
      do {
        const response = await inventoryApi.list({ limit: 200, skip: allProducts.length });
      if (!operationCurrent()) return;
        page = response.data || [];
        allProducts.push(...page);
      } while (page.length === 200);
      setProducts(allProducts);
    } catch (error) {
      if (!operationCurrent()) return;
      setLoadError(errorText(error));
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setLoading(false);
      }
    }
  }, [beginOperation]);

  const categoryOptions = [...new Set(products.map((product) => product.category).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const visibleProducts = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    let list = products.filter((product) => {
      if (query) {
        const variantCodes = (product.variants || []).flatMap((variant) => [variant.sku, variant.barcode]);
        const haystack = [product.name, product.sku, product.barcode_value, product.category, ...variantCodes].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (filters.category && product.category !== filters.category) return false;
      if (filters.product_type && (product.product_type || "other") !== filters.product_type) return false;
      if (filters.active === "true" && product.active === false) return false;
      if (filters.active === "false" && product.active !== false) return false;
      if (filters.pricing === "needs" && isPriced(product)) return false;
      if (filters.pricing === "priced" && !isPriced(product)) return false;
      if (filters.stock) {
        const state = product.stock_state;
        if (filters.stock === "in" && state !== "in") return false;
        if (filters.stock === "low" && !(product.low_inventory ?? (state === "low"))) return false;
        if (filters.stock === "attention" && state !== "out" && !(product.low_inventory ?? (state === "low")) && isPriced(product)) return false;
        if (filters.stock === "out" && state !== "out") return false;
      }
      return true;
    });
    const text = (value) => String(value ?? "");
    const numeric = (value) => (Number.isFinite(value) ? value : Number.POSITIVE_INFINITY);
    list = [...list].sort((a, b) => {
      switch (sort) {
        case "name_desc": return text(b.name).localeCompare(text(a.name));
        case "sku_asc": return text(a.sku).localeCompare(text(b.sku));
        case "price_asc": return numeric(a.price_minor) - numeric(b.price_minor);
        case "price_desc": return numeric(b.price_minor) - numeric(a.price_minor);
        case "qty_asc": return numeric(productInventoryQuantity(a)) - numeric(productInventoryQuantity(b));
        case "qty_desc": return numeric(productInventoryQuantity(b)) - numeric(productInventoryQuantity(a));
        case "name_asc":
        default: return text(a.name).localeCompare(text(b.name));
      }
    });
    return list;
  }, [products, filters, sort]);

  const squareInventoryRows = useMemo(
    () => inventoryPreviewRows(inventoryPreview),
    [inventoryPreview],
  );

  const activeFilterChips = [
    filters.search.trim() && { key: "search", label: `Search: ${filters.search.trim()}` },
    filters.category && { key: "category", label: `Category: ${filters.category}` },
    filters.product_type && { key: "product_type", label: `Type: ${({ round_sign: "Round sign", mason_jar: "Mason Jar", shadow_box: "Shadow box", swing: "Swing", other: "Other / review" })[filters.product_type] || filters.product_type}` },
    filters.active === "true" && { key: "active", label: "Status: Active" },
    filters.active === "false" && { key: "active", label: "Status: Inactive" },
    filters.stock === "in" && { key: "stock", label: "Stock: In stock" },
    filters.stock === "low" && { key: "stock", label: "Stock: Low" },
    filters.stock === "attention" && { key: "stock", label: "Needs attention: Out of stock, low stock, or needs pricing" },
    filters.stock === "out" && { key: "stock", label: "Stock: Out of stock" },
    filters.pricing === "needs" && { key: "pricing", label: "Pricing: Needs pricing" },
    filters.pricing === "priced" && { key: "pricing", label: "Pricing: Priced" },
  ].filter(Boolean);

  const resetFilters = () => setFilters({ search: "", category: "", product_type: "", active: "", stock: "", pricing: "" });
  const clearFilter = (key) => setFilters((current) => ({ ...current, [key]: "" }));

  // Use the same authoritative display predicate as rows and pricing filters.
  const needsPricingCount = products.filter((product) => !isPriced(product)).length;

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    const operationCurrent = beginOperation();
    let cancelled = false;
    inventoryApi
      .squareIntegrationStatus()
      .then((response) => {
        if (!cancelled && operationCurrent()) setSquareStatus(response.data);
      })
      .catch(() => {
        if (!cancelled && operationCurrent()) setSquareStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [beginOperation]);

  useEffect(() => {
    const operationCurrent = beginOperation();
    let active = true;
    let objectUrl = "";
    if (selected) {
      fetchAdminFile(`/admin/products/${selected.id}/barcode.svg`)
        .then((blob) => {
          if (!active || !operationCurrent()) return;
          objectUrl = URL.createObjectURL(blob);
          setBarcodeUrl(objectUrl);
        })
        .catch(() => { if (active && operationCurrent()) setBarcodeUrl(""); });
    } else {
      setBarcodeUrl("");
    }
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selected, beginOperation]);

  const updateForm = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const startCreate = () => {
    catalogReturn.current = null;
    setSelected(null);
    setEditing(false);
    setForm(EMPTY_FORM);
    setHistory(null);
    setShowCreateForm(true);
  };

  const startEdit = (product) => {
    catalogReturn.current = { productId: product.id, scrollY: window.scrollY || 0 };
    setSelected(product);
    setEditing(true);
    setForm(productToForm(product));
    setHistory(null);
    setShowCreateForm(false);
  };

  useEffect(() => {
    const operationCurrent = beginOperation();
    let cancelled = false;
    if (!targetProductId) { setTargetError(""); setTargetLoading(false); return; }
    if (targetProductId.length > 200 || /[\u0000-\u001f]/.test(targetProductId)) {
      setTargetLoading(false);
      setTargetError("That product link is invalid. Find the item in the product list instead.");
      return;
    }
    setTargetLoading(true);
    setTargetError("");
    inventoryApi.get(encodeURIComponent(targetProductId)).then(({ data }) => {
      if (cancelled || !operationCurrent()) return;
      if (!data || String(data.id) !== targetProductId) throw new Error("Product is unavailable.");
      setSelected(data); setEditing(true); setShowCreateForm(false); setForm(productToForm(data)); setHistory(null);
      if (targetVariantId && !(data.variants || []).some((variant) => String(variant.id) === targetVariantId)) {
        setTargetError("This size or variant is no longer available. The product is open so you can review its current options.");
      }
    }).catch(() => {
      if (!cancelled && operationCurrent()) { setTargetError("This product could not be opened. It may have been removed or you may not have access. Search the product list or refresh to try again."); setSelected(null); setEditing(false); }
    }).finally(() => { if (!cancelled && operationCurrent()) setTargetLoading(false); });
    return () => { cancelled = true; };
  }, [targetProductId, targetVariantId, beginOperation]);

  useEffect(() => {
    if (!targetProductId || String(selected?.id || "") !== targetProductId) return;
    const target = (targetVariantId
      ? Array.from(document.querySelectorAll("[data-inventory-variant]")).find((element) => element.getAttribute("data-inventory-variant") === targetVariantId)
      : document.getElementById("inventory-editor")) || document.getElementById("inventory-editor");
    target?.scrollIntoView?.({ block: "start", behavior: "auto" });
    target?.focus?.({ preventScroll: true });
  }, [selected?.id, targetProductId, targetVariantId]);

  useEffect(() => {
    if (!targetGalleryId || targetProductId || loading) return;
    if (loadError) { setTargetError("Product records could not be verified. Refresh before opening pricing."); return; }
    if (targetGalleryId.length > 200 || /[\u0000-\u001f]/.test(targetGalleryId)) {
      setTargetError("That gallery link is invalid."); return;
    }
    const matches = products.filter((product) => product.source_type === "gallery" && String(product.source_id) === targetGalleryId);
    if (matches.length !== 1) {
      setSelected(null); setEditing(false);
      setTargetError(matches.length ? "More than one product is linked to this gallery item. Review inventory before changing prices." : "No verified product is linked to this gallery item. Use the explicit gallery sync action, then refresh; no product has been created automatically.");
      return;
    }
    const product = matches[0];
    setSelected(product); setEditing(true); setShowCreateForm(false); setForm(productToForm(product)); setHistory(null); setTargetError("");
  }, [targetGalleryId, targetProductId, products, loading, loadError]);

  const saveProductType = async (product, productType) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSaving(true);
    try {
      const response = await inventoryApi.updateType(product.id, {
        product_type: productType,
      });
      if (!operationCurrent()) return;
      setSelected(response.data);
      setProducts((current) =>
        current.map((item) => (item.id === product.id ? response.data : item)),
      );
      // Keep the edit form in sync so a later "Save product" does not submit a
      // stale product_type that reverts the type (and its default pricing).
      setForm((current) => ({ ...current, product_type: response.data.product_type ?? productType }));
      const defaultPrice =
        response.data?.price_minor != null
          ? `$${minorToDollars(response.data.price_minor)}`
          : "the default product price";
      toast.success(
        `Product type saved. Default pricing is now set to ${defaultPrice}.`,
      );
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const initializeMasonJar = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected || !masonConfirmed) return;
    setSaving(true);
    try {
      const response = await inventoryApi.initializeMasonJar(selected.id, {
        confirm: true,
        overwrite_prices: masonOverwrite,
        idempotency_key: `mason-jar-${selected.id}-${Date.now()}`,
      });
      if (!operationCurrent()) return;
      toast.success(
        `Mason Jar sizes ready: ${response.data.created_variants?.length || 0} created, ${response.data.custom_prices_preserved?.length || 0} preserved.`,
      );
      const refreshed = await inventoryApi.get(selected.id);
      if (!operationCurrent()) return;
      setSelected(refreshed.data);
      setProducts((current) => current.map((item) => item.id === selected.id ? refreshed.data : item));
      setMasonConfirmed(false);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  // Commit an uncontrolled input on Enter (blurs, firing its onBlur save),
  // or revert it to its saved value on Escape.
  const handleCommitKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.target.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.target.value = event.target.defaultValue;
      event.target.blur();
    }
  };

  const updateVariant = async (variant, field, value) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    const draft = { ...variant, [field]: value };
    if (field === "price_minor" || field === "sale_price_minor") {
      const parsed = dollarsToMinor(value);
      if (parsed === null || parsed <= 0) {
        toast.error("Enter a valid positive dollar amount.");
        return;
      }
      draft[field] = parsed;
    }
    let mutationSubmitted = false;
    setSaving(true);
    try {
      const productId = selected.id;
      const fresh = (await inventoryApi.get(productId)).data;
      if (!operationCurrent()) return;
      const payload = variantPayloadFromFresh(fresh, variant.id, variant, field, draft[field], productId);
      mutationSubmitted = true;
      await inventoryApi.updateVariant(productId, variant.id, payload);
      if (!operationCurrent()) return;
      const confirmed = verifyVariantSave((await inventoryApi.get(productId)).data, variant.id, field, draft[field], productId);
      if (!operationCurrent()) return;
      setSelected(confirmed);
      setProducts((current) => current.map((item) => item.id === productId ? confirmed : item));
      toast.success("Variant saved.");
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error((!mutationSubmitted || error?.response?.status) ? errorText(error) : `${errorText(error)} ${UNVERIFIED_SAVE}`);
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const updateVariantInventory = async (variant, operation) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    setSaving(true);
    try {
      const response = await inventoryApi.applyVariantInventory(
        selected.id,
        variant.id,
        {
          operation,
          quantity: 1,
          idempotency_key: `variant-${selected.id}-${variant.id}-${operation}-${Date.now()}`,
        },
      );
      if (!operationCurrent()) return;
      setSelected(response.data);
      setProducts((current) => current.map((item) => item.id === selected.id ? response.data : item));
      toast.success(
        `${variant.name}: ${operation === "receive" ? "stock added" : "stock subtracted"}.`,
      );
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const showVariantBarcode = async (variant) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    try {
      const blob = await fetchAdminFile(
        `/admin/products/${selected.id}/variants/${variant.id}/barcode.svg`,
      );
      if (!operationCurrent()) return;
      const url = URL.createObjectURL(blob);
      setVariantBarcodeUrls((current) => {
        if (current[variant.id]) URL.revokeObjectURL(current[variant.id]);
        return { ...current, [variant.id]: url };
      });
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    }
  };

  const reviewPresets = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setPresetBusy(true);
    try {
      const response = await inventoryApi.previewPresets({
        overwrite_existing: overwriteExisting,
      });
      if (!operationCurrent()) return;
      setPresetPreview(response.data);
      setPresetConfirmed(false);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setPresetBusy(false);
      }
    }
  };

  const applyPresets = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!presetConfirmed) return;
    setPresetBusy(true);
    try {
      const response = await inventoryApi.applyPresets({
        confirm: true,
        dry_run: false,
        overwrite_existing: overwriteExisting,
        idempotency_key: `pricing-presets-${Date.now()}`,
      });
      if (!operationCurrent()) return;
      toast.success(
        `Pricing applied: ${(response.data.updated_products || []).length} updated.`,
      );
      setPresetPreview(null);
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setPresetBusy(false);
      }
    }
  };

  const saveProduct = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    event.preventDefault();
    let mutationSubmitted = false;
    setSaving(true);
    try {
      const payload = buildProductPayload(form, editing);
      let response;
      if (editing) {
        const { price_minor, sale_price_minor, cost_minor, ...catalogPayload } =
          payload;
        mutationSubmitted = true;
        response = await inventoryApi.update(selected.id, catalogPayload);
      if (!operationCurrent()) return;
        if (price_minor !== null) {
          const pricingPayload = { price_minor, sale_price_minor, cost_minor };
          response = await inventoryApi.updatePricing(
            selected.id,
            pricingPayload,
          );
      if (!operationCurrent()) return;
        }
      } else {
        mutationSubmitted = true;
        response = await inventoryApi.create(payload);
      if (!operationCurrent()) return;
      }
      // Confirm the intended values actually persisted before reporting success.
      const confirmed = (await inventoryApi.get(response.data.id)).data;
      if (!operationCurrent()) return;
      const expectedId = editing ? selected.id : response.data.id;
      const verification = { ...payload };
      if (editing && payload.price_minor === null) {
        delete verification.price_minor; delete verification.sale_price_minor; delete verification.cost_minor;
      }
      verifyProductSave(confirmed, verification, expectedId);
      toast.success(editing ? "Product updated." : "Product created.");
      setSelected(confirmed);
      setEditing(true);
      setForm(productToForm(confirmed));
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error((!mutationSubmitted || error?.response?.status) ? errorText(error) : `${errorText(error)} ${UNVERIFIED_SAVE}`);
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const syncGallery = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSaving(true);
    try {
      const response = await inventoryApi.syncGallery();
      if (!operationCurrent()) return;
      toast.success(
        `Gallery sync complete: ${response.data.created} created, ${response.data.updated} updated, ${response.data.deleted || 0} old test items removed.`,
      );
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const applyOperation = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    event.preventDefault();
    if (!selected) return;
    const quantity =
      operation.operation === "correction" ? null : Number(operation.quantity);
    const correctionDelta =
      operation.operation === "correction"
        ? Number(operation.correction_delta)
        : null;
    if (
      (quantity !== null &&
        (!Number.isSafeInteger(quantity) || quantity <= 0)) ||
      (correctionDelta !== null &&
        (!Number.isSafeInteger(correctionDelta) || correctionDelta === 0))
    ) {
      toast.error("Enter a valid whole-number inventory change.");
      return;
    }
    const idempotencyKey =
      operation.idempotency_key.trim() ||
      `inventory-${selected.id}-${Date.now()}`;
    setSaving(true);
    try {
      const movement = await inventoryApi.apply(selected.id, {
        operation: operation.operation,
        ...(quantity === null
          ? { correction_delta: correctionDelta }
          : { quantity }),
        idempotency_key: idempotencyKey,
        note: operation.note.trim() || null,
      });
      if (!operationCurrent()) return;
      setSelected((current) =>
        current
          ? {
              ...current,
              on_hand: movement.data.resulting_on_hand,
              reserved: movement.data.resulting_reserved,
              available:
                movement.data.resulting_on_hand -
                movement.data.resulting_reserved,
            }
          : current,
      );
      toast.success(
        `Inventory updated: ${movement.data.resulting_on_hand - movement.data.resulting_reserved} available.`,
      );
      setOperation({
        operation: "receive",
        quantity: "",
        correction_delta: "",
        note: "",
        idempotency_key: "",
      });
      const refreshed = await inventoryApi.get(selected.id);
      if (!operationCurrent()) return;
      setSelected(refreshed.data);
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const showHistory = async (type) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    try {
      const response =
        type === "movements"
          ? await inventoryApi.movements(selected.id)
          : await inventoryApi.priceHistory(selected.id);
      if (!operationCurrent()) return;
      setHistory({ type, items: response.data || [] });
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    }
  };

  const lookupCode = async (code) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const trimmed = String(code || "").trim();
    if (!trimmed) return;
    try {
      const response = await inventoryApi.lookup(trimmed);
      if (!operationCurrent()) return;
      const found = response.data;
      startEdit(found);
      const isVisible = visibleProducts.some((product) => product.id === found.id);
      if (!isVisible && (filters.search.trim() || filters.category || filters.product_type || filters.active || filters.stock || filters.pricing)) {
        toast.error("Product found but hidden by current filters.");
      } else {
        toast.success(`Found ${found.name || found.sku}.`);
      }
      setLookup("");
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    }
  };

  const findLookup = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    event.preventDefault();
    await lookupCode(lookup);
      if (!operationCurrent()) return;
  };

  const handleScan = (code) => {
    setScannerOpen(false);
    lookupCode(code);
  };

  const printLabel = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    try {
      const blob = await fetchAdminFile(`/admin/products/${selected.id}/label`);
      if (!operationCurrent()) return;
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    }
  };

  const printVariantLabel = async (variant) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    try {
      const blob = await fetchAdminFile(
        `/admin/products/${selected.id}/variants/${variant.id}/label`,
      );
      if (!operationCurrent()) return;
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    }
  };

  const reviewSquareCatalog = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.previewSquareCatalog({ include_images: true });
      if (!operationCurrent()) return;
      setSquarePreview(response.data);
      setSquareSyncConfirmed(false);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const reviewMissingBarcodes = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.previewMissingBarcodes({});
      if (!operationCurrent()) return;
      setBarcodeGenerationPreview(response.data);
      setBarcodeGenerationConfirmed(false);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const generateMissingBarcodes = async (productIds, variationId = null) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.generateMissingBarcodes({
        product_ids: productIds,
        variation_id: variationId,
        confirmation: true,
      });
      if (!operationCurrent()) return;
      toast.success(
        `Generated ${response.data.generated || 0} stable barcode${response.data.generated === 1 ? "" : "s"}.`,
      );
      await Promise.all([
        loadProducts(),
        reviewSquareCatalog(),
        reviewMissingBarcodes(),
      ]);
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const applySquareCatalog = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!squareSyncConfirmed) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.applySquareCatalog({
        confirmation: true,
        environment: squareStatus?.environment === "production" ? "production" : "sandbox",
        include_images: true,
        idempotency_key: `square-catalog-${Date.now()}`,
      });
      if (!operationCurrent()) return;
      toast.success(
        `Square synchronization complete: ${response.data.eligible || 0} eligible products reviewed.`,
      );
      setSquareSyncConfirmed(false);
      await reviewSquareCatalog();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const syncSingleProduct = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!selected) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.squareSyncProduct(selected.id, {
        confirmation: true,
        environment: squareStatus?.environment === "production" ? "production" : "sandbox",
        include_images: true,
        allow_create: true,
        idempotency_key: `square-sync-${selected.id}-${Date.now()}`,
      });
      if (!operationCurrent()) return;
      const entry = (response.data.results || [])[0] || {};
      if (entry.status === "conflict") {
        toast.error(`Needs review: ${entry.reason || "match conflict"}`);
      } else if (entry.status === "error") {
        toast.error(entry.image_error || entry.reason || "Square sync failed.");
      } else {
        toast.success(`Synced to Square (${entry.status}).`);
      }
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const reviewSquareInventory = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.squareInventoryPreview({});
      if (!operationCurrent()) return;
      setInventoryPreview(response.data);
      setInventorySyncConfirmed(false);
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  const applySquareInventory = async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (!inventorySyncConfirmed) return;
    setSquareBusy(true);
    try {
      const response = await inventoryApi.squareInventoryApply({
        confirmation: true,
        environment: squareStatus?.environment === "production" ? "production" : "sandbox",
        idempotency_key: `square-inventory-${Date.now()}`,
      });
      if (!operationCurrent()) return;
      const applied =
        response.data.synced ??
        response.data.applied ??
        response.data.updated ??
        response.data.changes_pushed ??
        0;
      toast.success(`Square stock sync complete: ${applied} variation${applied === 1 ? "" : "s"} updated.`);
      setInventoryPreview(null);
      setInventorySyncConfirmed(false);
      await loadProducts();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(errorText(error));
    } finally {
      if (operationCurrent()) {
      setSquareBusy(false);
      }
    }
  };

  return (
    <div
      className={`bbw-admin-inventory inventory-reference-page min-h-screen ${(showCreateForm || editing) ? "inventory-has-editor" : ""}`}
      data-testid="admin-inventory-page"
    >
      <AdminPageHero title="Products & stock" description="Keep pricing, inventory and readiness together." metrics={[
        { label: "products", value: loading || loadError ? "—" : products.length },
        { label: "need pricing", value: loading || loadError ? "—" : needsPricingCount },
        { label: "need stock attention", value: loading || loadError ? "—" : products.filter((product) => product.stock_state === "out" || product.low_inventory || product.stock_state === "low").length },
      ]}>
        <Button type="button" onClick={startCreate}><PackagePlus className="w-4 h-4 mr-2" /> New product</Button>
      </AdminPageHero>
      <div className="inventory-reference-workspace max-w-7xl mx-auto">
        {targetLoading && <p role="status" className="inventory-target-message">Opening the requested product…</p>}
        {targetError && <div role="alert" className="inventory-target-message">{targetError}</div>}
        <div className="inventory-workspace-grid grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          <section id="inventory-catalog" tabIndex={-1} className="inventory-catalog" aria-label="Product catalog">
            <h2 className="inventory-section-title">Products & stock</h2>
            <div className="mb-3">
              <label htmlFor="inventory-search" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">
                Search products by name, SKU, barcode, or category
              </label>
              <Input
                id="inventory-search"
                value={filters.search}
                onChange={(event) =>
                  setFilters({ ...filters, search: event.target.value })
                }
                placeholder="Search products by name, SKU, barcode, or category"
                className="w-full bg-zinc-900 border-stone-100/10 text-stone-100"
                data-testid="inventory-search"
              />
            </div>
            <details className="inventory-filter-options"><summary>Filter & sort products</summary><div className="inventory-filter-grid grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 mb-3">
              <div>
                <label htmlFor="inventory-filter-category" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Category</label>
                <select
                  value={filters.category}
                  onChange={(event) =>
                    setFilters({ ...filters, category: event.target.value })
                  }
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-filter-category" data-testid="filter-category"
                >
                  <option value="">All categories</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="inventory-filter-product-type" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Product type</label>
                <select
                  value={filters.product_type}
                  onChange={(event) => setFilters({ ...filters, product_type: event.target.value })}
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-filter-product-type" data-testid="filter-product-type"
                >
                  <option value="">All product types</option>
                  <option value="round_sign">Round sign</option>
                  <option value="mason_jar">Mason Jar</option>
                  <option value="shadow_box">Shadow box</option>
                  <option value="swing">Swing</option>
                  <option value="other">Other / review</option>
                </select>
              </div>
              <div>
                <label htmlFor="inventory-filter-status" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Product status</label>
                <select
                  value={filters.active}
                  onChange={(event) =>
                    setFilters({ ...filters, active: event.target.value })
                  }
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-filter-status" data-testid="filter-status"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div>
                <label htmlFor="inventory-filter-stock" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Stock</label>
                <select
                  value={filters.stock}
                  onChange={(event) =>
                    setFilters({ ...filters, stock: event.target.value })
                  }
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-filter-stock" data-testid="filter-stock"
                >
                  <option value="">All stock</option>
                  <option value="attention">Needs attention (stock or pricing)</option>
                  <option value="in">In stock</option>
                  <option value="low">Low stock</option>
                  <option value="out">Out of stock</option>
                </select>
              </div>
              <div>
                <label htmlFor="inventory-filter-pricing" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Pricing</label>
                <select
                  value={filters.pricing}
                  onChange={(event) =>
                    setFilters({ ...filters, pricing: event.target.value })
                  }
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-filter-pricing" data-testid="filter-pricing"
                >
                  <option value="">All</option>
                  <option value="needs">Needs pricing</option>
                  <option value="priced">Priced</option>
                </select>
              </div>
              <div>
                <label htmlFor="inventory-sort-by" className="mb-1 block text-xs uppercase tracking-wider text-stone-400">Sort by</label>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="w-full rounded-md bg-zinc-900 border border-stone-100/10 px-3 py-2 text-sm text-stone-200"
                  id="inventory-sort-by" data-testid="sort-by"
                >
                  <option value="name_asc">Name A–Z</option>
                  <option value="name_desc">Name Z–A</option>
                  <option value="sku_asc">SKU</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                  <option value="qty_asc">Quantity: low to high</option>
                  <option value="qty_desc">Quantity: high to low</option>
                </select>
              </div>
            </div></details>
            {(activeFilterChips.length > 0) && (
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="active-filter-chips">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key + chip.label}
                    type="button"
                    onClick={() => clearFilter(chip.key)}
                    className="rounded-full border border-stone-100/20 bg-zinc-900 px-3 py-1 text-xs text-stone-200 hover:bg-stone-100/10"
                    data-testid={`chip-${chip.key}`}
                  >
                    {chip.label} Ã
                  </button>
                ))}
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-full border border-[#D8A34B]/50 bg-transparent px-3 py-1 text-xs text-[#D8A34B] hover:bg-[#D8A34B]/10"
                  data-testid="reset-filters"
                >
                  Reset filters
                </button>
              </div>
            )}
            <div className="inventory-result-summary mb-3 flex items-center gap-3 text-sm text-stone-400">
              <span data-testid="result-count">{loading ? "Loading inventory…" : loadError ? "Inventory needs a retry" : `Showing ${visibleProducts.length} of ${products.length} products`}</span>
              {!loading && !loadError && <span className="rounded-full border border-[#D8A34B]/40 bg-[#D8A34B]/10 px-2 py-1 text-xs text-[#D8A34B]">
                Needs Pricing: {needsPricingCount}
              </span>}
            </div>
            <div className="inventory-product-list max-h-[calc(100vh-21rem)] min-h-[20rem] overflow-auto rounded-2xl border border-stone-100/10 bg-zinc-900/60">
              {loadError ? <div role="alert" className="inventory-load-error"><strong>Could not load inventory.</strong><p>{loadError}</p><Button type="button" onClick={loadProducts}>Retry inventory</Button></div> : loading ? (
                <p role="status" className="p-6 text-stone-400">Loading products...</p>
              ) : visibleProducts.length === 0 ? (
                <div className="p-6 text-stone-400">
                  <p>No products match the current filters.</p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-3 rounded-md border border-[#D8A34B]/50 bg-transparent px-4 py-2 text-sm text-[#D8A34B] hover:bg-[#D8A34B]/10"
                  >
                    Reset filters
                  </button>
                </div>
              ) : (
                <table className="inventory-product-table min-w-[760px] w-full text-left text-sm">
                  <thead className="border-b border-stone-100/10 text-stone-500 uppercase text-xs tracking-wider">
                    <tr>
                      <th className="p-4">Product</th>
                      <th className="p-4 whitespace-nowrap">Price</th>
                      <th className="p-4 whitespace-nowrap">Stock</th>
                      <th className="p-4 whitespace-nowrap">Status</th>
                      <th><span className="sr-only">Edit product details</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProducts.map((product) => (
                      <tr
                        key={product.id}
                        data-inventory-product={product.id}
                        onClick={() => startEdit(product)}
                        className={`inventory-reference-row border-b border-stone-100/5 cursor-pointer hover:bg-stone-100/5 ${selected?.id === product.id ? "is-selected bg-[#26C6D5]/10" : ""}`}
                      >
                        <td data-label="Item" className="inventory-item-cell p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-stone-100/10 bg-zinc-950">
                              <img
                                src={
                                  product.gallery_image_url ||
                                  "/branding/bbw-logo.jpg"
                                }
                                alt=""
                                className="h-full w-full object-cover"
                                onError={(event) => {
                                  event.currentTarget.src =
                                    "/branding/bbw-logo.jpg";
                                }}
                              />
                            </div>
                            <div className="min-w-0">
                              <button type="button" className="inventory-item-open text-stone-100 font-medium" onClick={(event) => { event.stopPropagation(); startEdit(product); }} aria-label={`Edit ${product.name || "Untitled gallery item"}`}>{product.name || "Untitled gallery item"}</button>
                              <div className="text-xs text-stone-500">
                                {product.sku} · {product.category}
                              </div>
                              {product.source_type === "gallery" && (
                                <div className="text-[10px] uppercase tracking-wider text-[#26C6D5]">
                                  Gallery item
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label="Price" className="p-4 whitespace-nowrap text-stone-200">
                          {isPriced(product) ? (
                            product.product_type === "mason_jar"
                              ? `From $${minorToDollars(product.from_price_minor)}`
                              : `$${minorToDollars(product.sale_price_minor ?? product.price_minor)}`
                          ) : (
                            <span className="inline-flex whitespace-nowrap rounded-full border border-[#D8A34B]/40 bg-[#D8A34B]/10 px-2 py-1 text-xs text-[#D8A34B]">
                              Needs Pricing
                            </span>
                          )}
                        </td>
                        <td data-label="Stock" className="p-4 whitespace-nowrap">
                          {product.product_type === "mason_jar" ? (
                            <div className="space-y-1">
                              {(product.variants || []).filter((variant) => variant.active !== false).map((variant) => {
                                const available = Math.max(0, Number(variant.on_hand || 0) - Number(variant.reserved || 0));
                                const status = variantAvailability({
                                  available,
                                  inventory_tracked: Boolean(variant.track_inventory),
                                  fulfillment_mode: variant.fulfillment_mode || product.fulfillment_mode,
                                });
                                if (variant.inventory_out_of_stock) {
                                  status.label = status.label === "Made to order" ? "Out of stock · Made to order" : "Out of stock";
                                }
                                return <div key={variant.id} className="text-xs text-stone-300"><span className="text-stone-100">{variant.name}:</span> <span className={status.label.startsWith("Out of stock") ? "text-rose-400" : status.label === "Made to order" ? "text-stone-400" : "text-stone-200"}>{status.label}</span></div>;
                              })}
                              <div className="border-t border-stone-100/10 pt-1 text-[10px] text-stone-500">Combined available: {productInventoryQuantity(product)}</div>
                            </div>
                          ) : (() => {
                            const state = product.stock_state;
                            if (state === "made") return <span className="text-stone-400">Made to order</span>;
                            const available = Number(product.available ?? 0);
                            return <span className={state === "out" ? "text-rose-400" : state === "low" ? "text-[#D8A34B]" : "text-stone-200"}>{available} in stock</span>;
                          })()}
                        </td>
                        <td data-label="Status" className="p-4 whitespace-nowrap text-xs uppercase tracking-wider">
                          {product.active !== false ? (
                            <span className="text-emerald-400">Active</span>
                          ) : (
                            <span className="text-stone-500">Inactive</span>
                          )}
                        </td>
                        <td className="inventory-edit-cell"><Button type="button" variant="outline" aria-label={`Edit details for ${product.name || "product"}`} onClick={(event) => { event.stopPropagation(); startEdit(product); }}><Pencil className="w-4 h-4 mr-2" />{isPriced(product) ? "Edit" : "Review pricing"}</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section id="inventory-editor" tabIndex={-1} className={`inventory-editor rounded-2xl border border-stone-100/10 bg-zinc-900/60 p-5 ${(showCreateForm || editing) ? "" : "inventory-editor-empty"}`} aria-label="Product editor">
            {(showCreateForm || editing) ? (
            <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl text-stone-100">
                {editing ? "Edit product" : "New product"}
              </h2>
              {(editing || showCreateForm) && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeEditor}
                  className="inventory-editor-back text-stone-400"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" /><span className="inventory-back-label">Back to products</span>
                </Button>
              )}
            </div>
            {selected?.gallery_image_url && <img className="inventory-editor-preview" src={selected.gallery_image_url} alt={selected.name || "Selected product"} />}
            <form onSubmit={saveProduct} className="space-y-4">
              <label className="inventory-field">Name<Input
                required
                value={form.name}
                onChange={(event) => updateForm("name", event.target.value)}
                placeholder="Name"
                className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
              /></label>
              <label className="inventory-field">Description<textarea
                value={form.description}
                onChange={(event) =>
                  updateForm("description", event.target.value)
                }
                placeholder="Description"
                className="min-h-20 w-full rounded-md bg-zinc-950/60 border border-stone-100/10 p-3 text-sm text-stone-100"
              /></label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="inventory-field">SKU<Input
                  required={!editing}
                  disabled={editing}
                  value={form.sku}
                  onChange={(event) => updateForm("sku", event.target.value)}
                  placeholder="SKU"
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
                <label className="inventory-field">Barcode (defaults to SKU)<Input
                  disabled={editing}
                  value={form.barcode_value}
                  onChange={(event) =>
                    updateForm("barcode_value", event.target.value)
                  }
                  placeholder="Barcode (defaults to SKU)"
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="inventory-field">Category<select
                  required
                  value={form.category}
                  onChange={(event) =>
                    updateForm("category", event.target.value)
                  }
                  className="flex h-9 w-full rounded-md border border-stone-100/10 bg-zinc-950/60 px-3 text-sm text-stone-100"
                >
                  {[...new Set([
                    ...GALLERY_CATEGORIES.filter((category) => category !== "All"),
                    ...products.map((product) => product.category),
                    form.category,
                  ].filter(Boolean))].map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select></label>
                <label className="inventory-field">Currency<Input
                  value={form.currency}
                  onChange={(event) =>
                    updateForm("currency", event.target.value)
                  }
                  placeholder="Currency"
                  maxLength={3}
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
              </div>
              <div className={`grid grid-cols-1 gap-3 ${editing ? "" : "sm:grid-cols-2"}`}>
                {!editing && <label className="inventory-field">Product type<select
                  value={form.product_type}
                  onChange={(event) =>
                    updateForm("product_type", event.target.value)
                  }
                  className="rounded-md bg-zinc-950/60 border border-stone-100/10 px-3 text-sm text-stone-100"
                >
                  <option value="round_sign">Round sign</option>
                  <option value="mason_jar">Mason Jar</option>
                  <option value="shadow_box">Shadow box</option>
                  <option value="swing">Swing</option>
                  <option value="other">Other / review</option>
                </select></label>}
                <label className="inventory-field">Price $ (blank uses default)<Input
                  value={form.price}
                  onChange={(event) => updateForm("price", event.target.value)}
                  placeholder="Price $ (blank uses default)"
                  inputMode="decimal"
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="inventory-field">Sale $<Input
                  value={form.sale_price}
                  onChange={(event) =>
                    updateForm("sale_price", event.target.value)
                  }
                  placeholder="Sale $"
                  inputMode="decimal"
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
                <label className="inventory-field">Cost $<Input
                  value={form.cost}
                  onChange={(event) => updateForm("cost", event.target.value)}
                  placeholder="Cost $"
                  inputMode="decimal"
                  className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                /></label>
              </div>
              <label className="inventory-field">Low-stock threshold<Input
                value={form.reorder_threshold}
                onChange={(event) =>
                  updateForm("reorder_threshold", event.target.value)
                }
                placeholder="Low-stock threshold"
                inputMode="numeric"
                className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
              /></label>
              <div className="flex flex-wrap gap-4 text-sm text-stone-300">
                <label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(event) =>
                      updateForm("active", event.target.checked)
                    }
                    className="mr-2"
                  />
                  Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={form.track_inventory}
                    onChange={(event) =>
                      updateForm("track_inventory", event.target.checked)
                    }
                    className="mr-2"
                  />
                  Track inventory
                </label>
              </div>
              <label className="block text-sm text-stone-300">
                Fulfillment policy
                <select
                  value={form.fulfillment_mode}
                  onChange={(event) =>
                    updateForm("fulfillment_mode", event.target.value)
                  }
                  className="mt-1 w-full rounded-md border border-stone-100/10 bg-zinc-950/60 p-2.5 text-stone-100"
                >
                  <option value="stocked">
                    Stocked — block when unavailable
                  </option>
                  <option value="hybrid">
                    Hybrid — use stock, then make more
                  </option>
                  <option value="made_to_order">
                    Made to order — always produce fresh
                  </option>
                </select>
              </label>
              <p className="inventory-policy-help">{({ stocked: "Stocked: customers cannot order when available stock runs out.", hybrid: "Hybrid: use available stock first, then make more to fulfill the order.", made_to_order: "Made to order: produce each order fresh, regardless of stock." })[form.fulfillment_mode]}</p>
              {editing && (
                <details className="rounded-xl border border-[#26C6D5]/20 bg-[#26C6D5]/5 p-4">
                  <summary className="cursor-pointer text-sm font-medium text-[#26C6D5]">
                    Customization settings
                  </summary>
                  <p className="mt-2 text-xs text-stone-400">
                    Existing customization data is preserved for this product.
                  </p>
                  <textarea
                    value={form.customization_json || ""}
                    onChange={(event) =>
                      updateForm("customization_json", event.target.value)
                    }
                    className="mt-3 min-h-40 w-full rounded-md border border-stone-100/10 bg-zinc-950 p-3 font-mono text-xs text-stone-100"
                  />
                </details>
              )}
              <Button
                disabled={saving}
                type="submit"
                className="w-full bg-[#D8A34B] text-[#15191C] hover:bg-[#c8963f]"
              >
                {saving ? "Saving..." : "Save product"}
              </Button>
            </form>

            {selected && editing && (
              <>
                {selected.gallery_image_url && (
                  <div className="mb-5 overflow-hidden rounded-xl border border-stone-100/10 bg-zinc-950">
                    <img
                      src={selected.gallery_image_url}
                      alt=""
                      className="max-h-56 w-full object-contain"
                      onError={(event) => {
                        event.currentTarget.src = "/branding/bbw-logo.jpg";
                      }}
                    />
                    <p className="px-3 py-2 text-xs uppercase tracking-wider text-stone-500">
                      Linked gallery image
                    </p>
                  </div>
                )}
                <div className="mt-6 border-t border-stone-100/10 pt-5">
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500">
                    Product type
                  </label>
                  {selected.suggested_product_type && (
                    <p className="mt-2 text-xs text-[#26C6D5]">
                      Suggested: {selected.suggested_product_type} (
                      {selected.suggestion_confidence || "review"}) -{" "}
                      {selected.suggestion_reason}
                    </p>
                  )}
                  <select
                    value={selected.product_type || "other"}
                    disabled={saving}
                    onChange={(event) =>
                      saveProductType(selected, event.target.value)
                    }
                    className="mt-2 w-full rounded-md bg-zinc-950/60 border border-stone-100/10 p-2.5 text-sm text-stone-100"
                  >
                    <option value="round_sign">Round sign</option>
                    <option value="mason_jar">
                      Mason Jar - variant pricing deferred
                    </option>
                    <option value="shadow_box">Shadow box</option>
                    <option value="swing">Swing</option>
                    <option value="other">Other / review</option>
                  </select>
                </div>
                {selected.product_type === "mason_jar" && (
                  <div className="mt-5 rounded-xl border border-[#26C6D5]/25 bg-[#26C6D5]/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#26C6D5]">
                        Mason Jar sizes
                      </p>
                      <span className="text-xs text-[#D8A34B]">
                        {selected.needs_pricing
                          ? "Needs Pricing"
                          : `From $${((selected.from_price_minor || 0) / 100).toFixed(2)}`}
                      </span>
                    </div>
                    <div className="mt-3 space-y-3">
                      {(selected.variants || []).map((variant) => (
                        <div
                          key={variant.id}
                          data-inventory-variant={variant.id}
                          tabIndex={-1}
                          className={`rounded-lg border border-stone-100/10 p-3 ${String(variant.id) === targetVariantId ? "inventory-target-variant" : ""}`}
                        >
                          <div className="grid gap-2 sm:grid-cols-2">
                            <Input
                              defaultValue={variant.name}
                              onKeyDown={handleCommitKeyDown}
                              onBlur={(event) =>
                                updateVariant(
                                  variant,
                                  "name",
                                  event.target.value,
                                )
                              }
                              className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                            />
                            <Input
                              defaultValue={variant.sku}
                              onKeyDown={handleCommitKeyDown}
                              onBlur={(event) =>
                                updateVariant(
                                  variant,
                                  "sku",
                                  event.target.value,
                                )
                              }
                              className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                            />
                            <Input
                              defaultValue={variant.barcode || ""}
                              placeholder="Barcode"
                              onKeyDown={handleCommitKeyDown}
                              onBlur={(event) =>
                                updateVariant(
                                  variant,
                                  "barcode",
                                  event.target.value,
                                )
                              }
                              className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                            />
                            <Input
                              defaultValue={
                                variant.price_minor == null
                                  ? ""
                                  : (variant.price_minor / 100).toFixed(2)
                              }
                              placeholder="Price $"
                              onKeyDown={handleCommitKeyDown}
                              onBlur={(event) =>
                                updateVariant(
                                  variant,
                                  "price_minor",
                                  event.target.value,
                                )
                              }
                              className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                            />
                            <label className="text-sm text-stone-300 sm:col-span-2">
                              Fulfillment policy
                              <select
                                value={variant.fulfillment_mode || "hybrid"}
                                onChange={(event) =>
                                  updateVariant(
                                    variant,
                                    "fulfillment_mode",
                                    event.target.value,
                                  )
                                }
                                className="mt-1 w-full rounded-md border border-stone-100/10 bg-zinc-950/60 p-2.5 text-stone-100"
                              >
                                <option value="stocked">Stocked — block when unavailable</option>
                                <option value="hybrid">Hybrid — use stock, then make more</option>
                                <option value="made_to_order">Made to order — always produce fresh</option>
                              </select>
                            </label>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-4 text-xs text-stone-300">
                            <label>
                              <input
                                type="checkbox"
                                defaultChecked={variant.active}
                                onChange={(event) =>
                                  updateVariant(
                                    variant,
                                    "active",
                                    event.target.checked,
                                  )
                                }
                                className="mr-2"
                              />
                              Active
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                defaultChecked={variant.track_inventory}
                                onChange={(event) =>
                                  updateVariant(
                                    variant,
                                    "track_inventory",
                                    event.target.checked,
                                  )
                                }
                                className="mr-2"
                              />
                              Track inventory
                            </label>
                            <span>On hand: {variant.on_hand || 0}</span>
                            <span>Reserved: {variant.reserved || 0}</span>
                            <span>
                              Available:{" "}
                              {(variant.on_hand || 0) - (variant.reserved || 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-xs text-stone-300">
                      <input
                        type="checkbox"
                        checked={masonOverwrite}
                        onChange={(event) =>
                          setMasonOverwrite(event.target.checked)
                        }
                      />{" "}
                      Reset existing variant prices to defaults
                    </label>
                    <label className="mt-2 flex items-center gap-2 text-xs text-stone-300">
                      <input
                        type="checkbox"
                        checked={masonConfirmed}
                        onChange={(event) =>
                          setMasonConfirmed(event.target.checked)
                        }
                      />{" "}
                      Confirm Mason Jar size setup
                    </label>
                    <Button
                      type="button"
                      onClick={initializeMasonJar}
                      disabled={saving || !masonConfirmed}
                      className="mt-3 bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2]"
                    >
                      Set up Mason Jar sizes
                    </Button>
                  </div>
                )}
                {selected.product_type === "mason_jar" && (
                  <div className="mt-6 border-t border-stone-100/10 pt-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">
                      Separate size inventory
                    </p>
                    <div className="space-y-3">
                      {(selected.variants || []).map((variant) => (
                        <div
                          key={`inventory-${variant.id}`}
                          className="rounded-lg border border-stone-100/10 bg-zinc-950/40 p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-stone-100">
                                {variant.name}
                              </p>
                              <p className="text-xs text-stone-500">
                                Barcode: {variant.barcode || variant.sku}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => showVariantBarcode(variant)}
                                className="bg-transparent border-stone-100/15 text-stone-300"
                              >
                                Show barcode
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => printVariantLabel(variant)}
                                className="bg-transparent border-stone-100/15 text-stone-300"
                              >
                                <Printer className="w-4 h-4 mr-2" /> Print
                                barcode label
                              </Button>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                            <div>
                              <p className="text-lg text-stone-100">
                                {variant.on_hand || 0}
                              </p>
                              <p className="text-stone-500">On hand</p>
                            </div>
                            <div>
                              <p className="text-lg text-stone-100">
                                {variant.reserved || 0}
                              </p>
                              <p className="text-stone-500">On hold</p>
                            </div>
                            <div>
                              <p className="text-lg text-[#26C6D5]">
                                {(variant.on_hand || 0) -
                                  (variant.reserved || 0)}
                              </p>
                              <p className="text-stone-500">Available</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                updateVariantInventory(variant, "receive")
                              }
                              className="bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2]"
                            >
                              Add 1
                            </Button>
                            <Button
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                updateVariantInventory(variant, "remove")
                              }
                              variant="outline"
                              className="bg-transparent border-rose-400/40 text-rose-200"
                            >
                              Remove 1
                            </Button>
                          </div>
                          {variantBarcodeUrls[variant.id] && (
                            <div className="mt-3 rounded bg-white p-2">
                              <img
                                src={variantBarcodeUrls[variant.id]}
                                alt={`Barcode for ${variant.name}`}
                                className="w-full max-h-20"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-6 border-t border-stone-100/10 pt-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">
                    {selected.product_type === "mason_jar" ? "Combined size stock" : "Stock summary"}
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-2xl text-stone-100">
                        {selected.on_hand}
                      </p>
                      <p className="text-xs text-stone-500">On hand</p>
                    </div>
                    <div>
                      <p className="text-2xl text-stone-100">
                        {selected.reserved}
                      </p>
                      <p className="text-xs text-stone-500">Reserved</p>
                    </div>
                    <div>
                      <p className="text-2xl text-[#26C6D5]">
                        {selected.available}
                      </p>
                      <p className="text-xs text-stone-500">Available</p>
                    </div>
                  </div>
                </div>
                {selected.product_type !== "mason_jar" && selected.available <= 0 && (
                  <div className="mt-5 rounded-xl border border-rose-400/40 bg-rose-950/25 p-4">
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-rose-200">
                      Make more
                    </p>
                    <p className="mt-1 text-sm text-rose-100/80">
                      This product has no available inventory. Receive stock
                      when more are ready.
                    </p>
                  </div>
                )}
                {selected.product_type !== "mason_jar" && <form onSubmit={applyOperation} className="mt-5 space-y-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                    Quick inventory update
                  </p>
                  <select
                    value={operation.operation}
                    onChange={(event) =>
                      setOperation({
                        ...operation,
                        operation: event.target.value,
                      })
                    }
                    className="w-full rounded-md bg-zinc-950/60 border border-stone-100/10 p-2.5 text-sm text-stone-100"
                  >
                    <option value="receive">Add stock</option>
                    <option value="remove">Subtract stock</option>
                    <option value="reserve">Put on hold</option>
                    <option value="release">Release hold</option>
                    <option value="correction">Correct total</option>
                  </select>
                  {operation.operation === "correction" ? (
                    <label className="inventory-field">Signed correction delta<Input
                      value={operation.correction_delta}
                      onChange={(event) =>
                        setOperation({
                          ...operation,
                          correction_delta: event.target.value,
                        })
                      }
                      placeholder="Signed correction delta"
                      inputMode="numeric"
                      className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                    /></label>
                  ) : (
                    <label className="inventory-field">How many?<Input
                      value={operation.quantity}
                      onChange={(event) =>
                        setOperation({
                          ...operation,
                          quantity: event.target.value,
                        })
                      }
                      placeholder="How many?"
                      inputMode="numeric"
                      className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                    /></label>
                  )}
                  <label className="inventory-field">Optional note<Input
                    value={operation.note}
                    onChange={(event) =>
                      setOperation({ ...operation, note: event.target.value })
                    }
                    placeholder="Optional note"
                    className="bg-zinc-950/60 border-stone-100/10 text-stone-100"
                  /></label>
                  <Button
                    disabled={saving}
                    type="submit"
                    variant="outline"
                    className="w-full bg-transparent border-[#26C6D5]/40 text-[#26C6D5]"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />{" "}
                    {operation.operation === "receive"
                      ? "Add stock"
                      : operation.operation === "remove"
                        ? "Subtract stock"
                        : "Save inventory update"}
                  </Button>
                </form>}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => showHistory("movements")}
                    className="bg-transparent border-stone-100/15 text-stone-300"
                  >
                    Movement history
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => showHistory("pricing")}
                    className="bg-transparent border-stone-100/15 text-stone-300"
                  >
                    Pricing history
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={printLabel}
                    className="bg-transparent border-stone-100/15 text-stone-300"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print label
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={syncSingleProduct}
                    disabled={squareBusy}
                    className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10"
                    data-testid="sync-single-square" data-quiet-action="true"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Sync to Square
                  </Button>
                </div>
                {barcodeUrl && (
                  <div className="mt-5 rounded-lg bg-white p-3">
                    <img
                      src={barcodeUrl}
                      alt={`Internal barcode for ${selected.sku}`}
                      className="w-full h-auto"
                    />
                  </div>
                )}
                {history && (
                  <div className="mt-5 max-h-64 overflow-auto border-t border-stone-100/10 pt-4 text-xs text-stone-300">
                    <div className="flex items-center justify-between mb-2">
                      <p className="uppercase tracking-wider text-stone-500">
                        {history.type}
                      </p>
                      <button
                        type="button"
                        onClick={() => setHistory(null)}
                        aria-label="Close history"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {history.items.length === 0 ? (
                      <p className="text-stone-500">No history yet.</p>
                    ) : (
                      history.items.map((item) => (
                        <pre
                          key={item.id}
                          className="whitespace-pre-wrap border-b border-stone-100/5 py-2"
                        >
                          {JSON.stringify(item, null, 2)}
                        </pre>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            </>
            ) : (
              <div className="flex min-h-[10rem] flex-col items-center justify-center gap-3 text-center text-stone-500">
                <PackagePlus className="h-8 w-8 text-stone-600" />
                <p className="text-sm">Select a product to edit, or add a new one.</p>
                <Button
                  type="button"
                  onClick={startCreate}
                  className="bg-[#D8A34B] text-[#15191C] hover:bg-[#c8963f]"
                >
                  <PackagePlus className="w-4 h-4 mr-2" /> Add product
                </Button>
              </div>
            )}
          </section>
        </div>
        <div className="inventory-secondary-tools" aria-label="Additional inventory tools">
        <details className="inventory-tools mb-6 border-y border-stone-100/10 py-5"><summary><span>Square & barcode tools</span><small>Preview syncs and review bulk changes</small></summary><div className="inventory-tools-body"><div className="inventory-header-actions">
            <Button
              type="button"
              onClick={syncGallery}
              disabled={saving}
              variant="outline"
              className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5]"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Sync gallery
            </Button>
            <Button
              type="button"
              onClick={reviewPresets}
              disabled={presetBusy}
              variant="outline"
              className="bg-transparent border-[#D8A34B]/50 text-[#D8A34B]"
            >
              Review pricing presets
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={loadProducts}
              className="bg-transparent border-stone-100/15 text-stone-200"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#26C6D5]">
                Square catalog
              </p>
              <h2 className="font-display text-2xl text-stone-100">
                Barcode synchronization
              </h2>
              {squareStatus && (
                <p className="mt-1 text-sm text-stone-400">
                  Square {squareStatus.environment === "production" ? "Production" : "Sandbox"} configured
                  {" "}&middot; access token {squareStatus.accessTokenConfigured ? "present" : "missing"}
                  {" "}&middot; location {squareStatus.locationIdConfigured ? "present" : "missing"}
                  {" "}&middot; webhook signature {squareStatus.webhookSignatureKeyConfigured ? "present" : "missing"}
                  {" "}&middot; webhook URL {squareStatus.webhookNotificationUrlConfigured ? "present" : "missing"}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={squareBusy}
                onClick={reviewSquareCatalog}
                className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5]"
              >
                Preview Square upload
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={squareBusy}
                onClick={reviewMissingBarcodes}
                className="bg-transparent border-[#D8A34B]/50 text-[#D8A34B]"
              >
                Generate all missing barcodes
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={squareBusy}
                onClick={reviewSquareInventory}
                className="bg-transparent border-emerald-500/40 text-emerald-300"
                data-testid="preview-square-inventory"
              >
                Preview stock sync
              </Button>
            </div>
          </div>
          {barcodeGenerationPreview && (
            <div className="mt-4 border-t border-stone-100/10 pt-4">
              <p className="text-sm text-stone-300">
                Dry run: {barcodeGenerationPreview.proposed?.length || 0}{" "}
                missing barcode
                {barcodeGenerationPreview.proposed?.length === 1 ? "" : "s"};{" "}
                {barcodeGenerationPreview.blocked?.length || 0} blocked.
              </p>
              {barcodeGenerationPreview.proposed?.length > 0 && (
                <>
                  <label className="mt-3 flex items-center gap-2 text-sm text-stone-200">
                    <input
                      type="checkbox"
                      checked={barcodeGenerationConfirmed}
                      onChange={(event) =>
                        setBarcodeGenerationConfirmed(event.target.checked)
                      }
                    />{" "}
                    I reviewed the dry run and confirm bulk generation.
                  </label>
                  <Button
                    type="button"
                    disabled={squareBusy || !barcodeGenerationConfirmed}
                    onClick={() => generateMissingBarcodes(null)}
                    className="mt-3 bg-[#D8A34B] text-[#15191C]"
                  >
                    Confirm generation
                  </Button>
                </>
              )}
            </div>
          )}
          {squarePreview && (
            <div className="mt-5">
              <div className="overflow-auto border border-stone-100/10">
                <table className="min-w-[1050px] w-full text-left text-xs">
                  <thead className="border-b border-stone-100/10 text-stone-500 uppercase">
                    <tr>
                      <th className="p-3">Product</th>
                      <th className="p-3">Variation</th>
                      <th className="p-3">SKU</th>
                      <th className="p-3">Barcode value</th>
                      <th className="p-3">Barcode mode</th>
                      <th className="p-3">Square field</th>
                      <th className="p-3">Conflict</th>
                      <th className="p-3">Scannable status</th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {squarePreview.products?.flatMap((product) =>
                      product.variations.map((variation) => (
                        <tr
                          key={`${product.product_id}-${variation.variation_id || "standard"}`}
                          className="border-b border-stone-100/5 text-stone-300"
                        >
                          <td className="p-3 text-stone-100">{product.name}</td>
                          <td className="p-3">{variation.name}</td>
                          <td className="p-3 font-mono">
                            {variation.sku || "-"}
                          </td>
                          <td className="p-3 font-mono">
                            {variation.barcode_value || "-"}
                          </td>
                          <td className="p-3">
                            {variation.barcode_classification}
                          </td>
                          <td className="p-3">
                            {variation.square_destination_field || "-"}
                          </td>
                          <td className="p-3">
                            {variation.duplicate_conflict_status?.join(", ") ||
                              "None"}
                          </td>
                          <td
                            className={`p-3 ${variation.scannable ? "text-emerald-300" : "text-rose-300"}`}
                          >
                            {variation.status_label}
                          </td>
                          <td className="p-3">
                            {variation.barcode_classification === "missing" && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={squareBusy}
                                onClick={() =>
                                  generateMissingBarcodes(
                                    [product.product_id],
                                    variation.variation_id,
                                  )
                                }
                                className="bg-transparent border-[#D8A34B]/40 text-[#D8A34B]"
                              >
                                Generate missing barcode
                              </Button>
                            )}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-stone-200">
                <input
                  type="checkbox"
                  checked={squareSyncConfirmed}
                  onChange={(event) =>
                    setSquareSyncConfirmed(event.target.checked)
                  }
                />{" "}
                I reviewed the preview and confirm the Square upload.
              </label>
              <Button
                type="button"
                onClick={applySquareCatalog}
                disabled={
                  squareBusy || !squareSyncConfirmed || !squarePreview.eligible
                }
                className="mt-3 bg-[#26C6D5] text-[#15191C]"
              >
                Synchronize eligible products
              </Button>
            </div>
          )}

          {inventoryPreview && (
            <div className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-950/10 p-4" data-testid="inventory-preview">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-stone-100">Stock sync preview</h3>
                <Button type="button" variant="ghost" onClick={() => setInventoryPreview(null)} className="text-stone-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="mt-1 text-sm text-stone-400">
                {squareInventoryRows.length} variations reviewed · {inventoryPreview.needs_sync ?? 0} need sync · {inventoryPreview.conflicts ?? 0} conflicts
              </p>
              <div className="mt-3 max-h-64 overflow-auto">
                <table className="min-w-[680px] w-full text-left text-sm">
                  <thead className="text-stone-500 uppercase text-xs">
                    <tr>
                      <th className="p-2">Product / Variation</th>
                      <th className="p-2">SKU</th>
                      <th className="p-2">Website</th>
                      <th className="p-2">Square</th>
                      <th className="p-2">Tracking</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {squareInventoryRows.map((v, index) => (
                      <tr key={index} className="border-t border-stone-100/5">
                        <td className="p-2 text-stone-100">{v.product}{v.variation ? ` — ${v.variation}` : ""}</td>
                        <td className="p-2 text-stone-400">{v.sku}</td>
                        <td className="p-2 text-stone-200">{v.website_quantity ?? "—"}</td>
                        <td className="p-2 text-stone-200">{v.square_quantity ?? "—"}</td>
                        <td className="p-2 text-stone-400">{v.track_inventory ? "Tracked" : "Not tracked"}</td>
                        <td className={`p-2 ${inventoryPreviewStatusClass(v)}`}>
                          {inventoryPreviewStatus(v)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-stone-200">
                <input
                  type="checkbox"
                  checked={inventorySyncConfirmed}
                  onChange={(event) => setInventorySyncConfirmed(event.target.checked)}
                />{" "}
                I reviewed the preview and confirm the Square stock update.
              </label>
              <Button
                type="button"
                onClick={applySquareInventory}
                disabled={squareBusy || !inventorySyncConfirmed}
                className="mt-3 bg-emerald-500 text-[#15191C]"
                data-testid="apply-square-inventory"
              >
                Apply stock to Square
              </Button>
            </div>
          )}
        </div></details>

        {presetPreview && (
          <section className="mb-6 rounded-2xl border border-[#D8A34B]/30 bg-[#D8A34B]/5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-2xl text-stone-100">
                Pricing preset review
              </h2>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPresetPreview(null)}
                className="text-stone-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="mt-2 text-sm text-stone-300">
              Reviewed {presetPreview.total_products_reviewed || 0} products. No
              changes have been made.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm text-stone-300">
              <span>
                Proposed: {presetPreview.matched_products?.length || 0}
              </span>
              <span>
                Preserved custom:{" "}
                {presetPreview.custom_prices_preserved?.length || 0}
              </span>
              <span>
                Mason Jars deferred:{" "}
                {presetPreview.mason_jar_deferred?.length || 0}
              </span>
              <span>
                Unclassified: {presetPreview.manual_classification?.length || 0}
              </span>
            </div>
            {presetPreview.matched_products?.length > 0 && (
              <div className="mt-4 max-h-56 overflow-auto rounded-lg border border-stone-100/10">
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 border-b border-stone-100/10 p-3 text-xs uppercase tracking-wider text-stone-500">
                  <span>Product</span>
                  <span>Type</span>
                  <span>Price</span>
                </div>
                {presetPreview.matched_products.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_auto_auto] gap-3 border-b border-stone-100/5 p-3 text-sm text-stone-200"
                  >
                    <span>{item.name}</span>
                    <span>{item.product_type}</span>
                    <span>
                      ${(Number(item.price_minor || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <label className="mt-4 flex items-center gap-2 text-sm text-stone-200">
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(event) => setOverwriteExisting(event.target.checked)}
              />{" "}
              Overwrite existing custom prices
            </label>
            {overwriteExisting && (
              <p className="mt-2 text-sm text-rose-300">
                This will replace existing custom prices for eligible products.
              </p>
            )}
            <label className="mt-4 flex items-center gap-2 text-sm text-stone-200">
              <input
                type="checkbox"
                checked={presetConfirmed}
                onChange={(event) => setPresetConfirmed(event.target.checked)}
              />{" "}
              I reviewed these changes and confirm applying them.
            </label>
            <Button
              type="button"
              onClick={applyPresets}
              disabled={presetBusy || !presetConfirmed}
              className="mt-4 bg-[#D8A34B] text-[#15191C] hover:bg-[#c8963f]"
            >
              Apply pricing presets
            </Button>
          </section>
        )}

        <details className="inventory-scan-tools"><summary>Scan or enter barcode</summary><form
          onSubmit={findLookup}
          className="inventory-lookup rounded-2xl border border-[#26C6D5]/25 bg-[#26C6D5]/5 p-4 mb-6 flex flex-wrap gap-3 items-center"
        >
          <label htmlFor="inventory-barcode-lookup" className="inventory-lookup-label"><Barcode className="w-5 h-5 text-[#26C6D5]" /> Find an item by barcode or SKU</label>
          <Input
            id="inventory-barcode-lookup"
            ref={lookupRef}
            value={lookup}
            onChange={(event) => setLookup(event.target.value)}
            placeholder="Scan or enter SKU / internal barcode"
            className="bg-zinc-950/60 border-stone-100/10 text-stone-100 flex-1 min-w-[240px]"
            aria-label="Scan or enter SKU"
          />
          <Button
            type="submit"
            className="bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2]"
          >
            <Search className="w-4 h-4 mr-2" /> Find product
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setScannerOpen(true)}
            className="bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10"
            data-testid="scan-with-camera"
          >
            <Barcode className="w-4 h-4 mr-2" /> Scan with camera
          </Button>
        </form></details>

        {scannerOpen && (
          <BarcodeScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />
        )}

        </div>
      </div>
    </div>
  );
}
